import Button from '@/components/UI/Button';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative';
import { modalAction } from '@/utils/modal';
import { FC, useMemo, useState } from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form-latest';
import { useDispatch } from 'react-redux';
import { CageConsumptionMetricApiId, ConsumptionMetric } from '@/model/Consumption.model.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { _notNil } from '@/littledash.ts';
import { IntakeInputsForm } from '@/components/Workflow/Show/Views/Consumption/IntakeInputsForm.tsx';
import { parseFormFields } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';
import { EditConsumptionRequest } from '@/components/Workflow/Show/WorkflowConsumption.tsx';
import { errorToast } from '@/helpers.tsx';

interface EditCageMetricProps {
  handleSave: (consumptionMetricApiId: CageConsumptionMetricApiId, request: EditConsumptionRequest) => void;
  currentMetric: ConsumptionMetric;
  previousMetric: ConsumptionMetric | undefined;
  canEdit: boolean;
}

interface EditFormData {
  food: { fields: Record<string, number | string | null> };
  fluid: { fields: Record<string, number | string | null> };
  reason_for_change: string | null;
}

export const EditCageMetric: FC<EditCageMetricProps> = ({ handleSave, currentMetric, previousMetric, canEdit }) => {
  const [submissionError, setSubmissionError] = useState<Error>();
  const dispatch = useDispatch();
  const { closeModal } = modalAction(dispatch);

  const { cageMetricStatus, lastIn } = useMemo(() => {
    const cageMetricStatus = previousMetric?.status ?? 'end';
    const lastIn = previousMetric?.consumption_in ?? null;
    return { cageMetricStatus, lastIn };
  }, [previousMetric]);

  const { inputInDisabled, inputOutDisabled, defaultValues } = useMemo(() => {
    let inputInDisabled = !canEdit || (currentMetric.status == 'end' && _notNil(currentMetric.consumption_in));
    const inputOutDisabled = !canEdit || currentMetric.status == 'initial';
    let input_in = currentMetric.consumption_in ?? null;
    if (new Date(currentMetric.recorded_at).getDate() == new Date(previousMetric?.recorded_at ?? '').getDate()) {
      input_in = previousMetric?.consumption_in ?? null;
      inputInDisabled = true;
    }
    const defaultValues = {
      [currentMetric.type]: {
        fields: {
          input_out: currentMetric.consumption_out ?? null,
          input_in: input_in,
          spillage: currentMetric.spillage ?? null,
          comment: currentMetric.comment ?? null,
          population: currentMetric.population ?? null,
          reason_for_change: null,
        },
      },
    };
    return { inputInDisabled, inputOutDisabled, defaultValues };
  }, [currentMetric, previousMetric]);

  const formMethods: UseFormReturn<EditFormData> = useForm<EditFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    register,
  } = formMethods;

  const onSubmit = async (data: EditFormData) => {
    const fields = data[currentMetric.type]?.fields || {};
    const variables = parseFormFields(
      { fields },
      inputInDisabled,
      inputOutDisabled,
      lastIn,
      currentMetric.type,
      null,
      data.reason_for_change
    );
    if (variables.errorMessage) {
      errorToast(variables.errorMessage);
      return;
    }
    try {
      handleSave(currentMetric.api_id, { variables });
      closeModal();
    } catch (error) {
      setSubmissionError(error as Error);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {submissionError ? (
          <ApiErrorBanner
            className="mw6 mb4"
            error={submissionError}
            title="There was an error with your submission"
            text="Please correct the form errors below and try again. If this error persists try again later or contact support."
          />
        ) : null}
        <div className="center mv3 pv4 bg-white br2 shadow-4" style={{ maxWidth: '480px' }}>
          <div className="mb3 ph4">
            <h3 className="normal lh-title near-black basier-med f4">{'Food Intake'}</h3>
          </div>
          <div className="ph4">
            <div className="flex justify-between">
              <div className="pb3">
                <label>Date recorded</label>
                <DateTimePickerNative value={currentMetric.created_at} disabled={true} />
              </div>
              <div className="pb3">
                <label>Population</label>
                <input
                  type="text"
                  disabled={true}
                  {...register(`${currentMetric.type}.fields.population`, {
                    required: false,
                  })}
                />
              </div>
            </div>
            <IntakeInputsForm
              cageMetricStatus={cageMetricStatus}
              inputInDisabled={inputInDisabled}
              inputOutDisabled={inputOutDisabled}
              lastIn={lastIn}
              todayRecord={currentMetric}
              type={currentMetric.type}
              editing={true}
            />
          </div>
          <div className="ph4 pt3">
            <Button disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            <Button className="ml3" plain onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditCageMetric;
